/**
 * Dismissible — React (web) implementation.
 */

/// <reference lib="dom" />

import React, { useCallback, useState, type ReactNode } from 'react';
import { spacing } from '../../elements/spacing';
import type { DismissibleProps } from './Dismissible';
import { DismissibleClose } from './DismissibleClose.web';
import { getDismissalStore, useDismissed } from './dismissal-store';

export interface WebDismissibleProps extends DismissibleProps {
  children?: ReactNode;
  'data-testid'?: string;
  /**
   * Where the close button sits in document order (its drawn position is the
   * top-right corner either way). `start` makes it the first stop when tabbing
   * in, for content a member may want to skip, such as a rotating card.
   */
  closePlacement?: 'start' | 'end';
}

const VISUALLY_HIDDEN: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
};

export function Dismissible({
  dismissKey,
  close,
  dismissedAnnouncement,
  testID,
  children,
  'data-testid': dataTestId,
  closePlacement = 'end',
}: WebDismissibleProps) {
  const dismissed = useDismissed(dismissKey);
  const [dismissedHere, setDismissedHere] = useState(false);
  const dismiss = useCallback(() => {
    getDismissalStore().dismiss(dismissKey);
    setDismissedHere(true);
  }, [dismissKey]);
  if (dismissed) {
    // A polite live region carries the dismissal to screen readers once the
    // content (and its focused control) has gone.
    return dismissedHere && dismissedAnnouncement ? (
      <div role="status" aria-live="polite" style={VISUALLY_HIDDEN}>
        {dismissedAnnouncement}
      </div>
    ) : null;
  }
  if (!close) return <div data-testid={dataTestId ?? testID}>{children}</div>;
  const closeActionId = close.actionId;
  // Placed first, the button would paint under any later positioned or
  // translucent content (a cross-fade), so it is lifted above it.
  const closeFirst = closePlacement === 'start';
  const closeButton = (
    <div
      style={{
        position: 'absolute',
        top: spacing.sm,
        right: spacing.sm,
        ...(closeFirst ? { zIndex: 1 } : null),
      }}
    >
      <DismissibleClose
        accessibilityLabel={close.accessibilityLabel}
        actionId={closeActionId}
        testID={close.testID}
        onPress={dismiss}
      />
    </div>
  );
  return (
    <div data-testid={dataTestId ?? testID} style={{ position: 'relative' }}>
      {closeFirst ? closeButton : null}
      {children}
      {closeFirst ? null : closeButton}
    </div>
  );
}
